import { useParams, useNavigate } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { PatientInfo } from '@/components/features/patients/PatientInfo';
import { ActivityList } from '@/components/features/activities/ActivityList';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { usePatient } from '@/hooks/usePatients';
import { useActivities } from '@/hooks/useActivities';
import { getPatientFullName } from '@/types/patient';

export function PatientDetail() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { patient, isLoading: isLoadingPatient } = usePatient(patientId);
  const {
    activities,
    isLoading: isLoadingActivities,
    filters,
    updateFilters,
    clearFilters,
  } = useActivities({ patientId });

  if (isLoadingPatient) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!patient) {
    return (
      <EmptyState
        title="Patient not found"
        description="The requested patient could not be found"
        action={
          <Button onClick={() => navigate('/doctor/patients')}>
            Back to Patients
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <PageHeader
        title={getPatientFullName(patient)}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate('/doctor/patients')}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back
            </Button>
          </div>
        }
      />

      <div className="mb-6">
        <PatientInfo patient={patient} />
      </div>

      <ActivityList
        activities={activities}
        isLoading={isLoadingActivities}
        filters={filters}
        onFiltersChange={updateFilters}
        onFiltersClear={clearFilters}
        showFilters
      />
    </div>
  );
}
